import { useRecoilCallback } from 'recoil';
import type { OpeningRanges } from '@kiute-tech/network-kiute-pro';
import {
    AgendaBooking,
    AgendaDiscount,
    useLogicalDaysAtom,
    useFilteredAgendaBookings,
    useLogicalDayLoading,
    useFilteredAgendaDiscounts,
    CombinedLogicalDaysResponse,
    useResourceOpeningRanges,
} from '@states/logicalDay';
import { useAgendaSettingsAtom, useSelectedDate } from '@states/agenda';
import { convertDateToDayId, getDatesBasedOnView } from '@utils/dates/dates';
import { getLogicalDaysByResourceIds } from '@utils/network/logicalday';
import { getPhysicalResourceCombinedId } from '@utils/network/resources';
import { WINDOW_MD_PX } from '@utils/constants';
import {
    AgendaResource,
    useSelectedResourcesByAgendaView,
} from '@states/resources';

export type UseLogicalDay = () => {
    isLoading: boolean;
    filteredAgendaAppointments: AgendaBooking[];
    filteredAgendaDiscounts: AgendaDiscount[];
    fetchCurrentLogicalDay: (
        selectedResources?: AgendaResource[],
    ) => Promise<void>;
};

export type groupOpeningRangesType = Record<
string,
Record<string, OpeningRanges>
>;
export type logicalDaysType = Record<string, CombinedLogicalDaysResponse>;

export const initLogicalDay = (dates: Date[] = []): logicalDaysType =>
    dates.reduce((acc, date) => {
        const dayId = convertDateToDayId(date);
        acc[dayId] = {
            hr: [],
            pr: [],
        };
        return acc;
    }, {} as logicalDaysType);

export const groupLogicalDaysByDayId = (
    logicalDaysRes: CombinedLogicalDaysResponse,
    dates: Date[],
): {
    logicalDays: logicalDaysType;
    groupOpeningRanges: groupOpeningRangesType;
} => {
    const logicalDays = initLogicalDay(dates);
    const groupOpeningRanges = {} as groupOpeningRangesType;

    logicalDaysRes?.hr.forEach((item) => {
        if (logicalDays[item.dayId]) {
            logicalDays[item.dayId].hr = [...logicalDays[item.dayId].hr, item];

            groupOpeningRanges[item.dayId] = {
                ...(groupOpeningRanges[item.dayId]
                    ? groupOpeningRanges[item.dayId]
                    : {}),
                [item.humanResourceId || -1]: item.openingRanges,
            };
        }
    });

    logicalDaysRes?.pr.forEach((item) => {
        if (logicalDays[item.dayId]) {
            logicalDays[item.dayId].pr = [...logicalDays[item.dayId].pr, item];

            groupOpeningRanges[item.dayId] = {
                ...(groupOpeningRanges[item.dayId]
                    ? groupOpeningRanges[item.dayId]
                    : {}),
                [getPhysicalResourceCombinedId(
                    item.physicalResourceId,
                    item.prUnitId,
                )]: item.openingRanges,
            };
        }
    });

    return { logicalDays, groupOpeningRanges };
};

const useLogicalDay: UseLogicalDay = () => {
    const { value: isLoading, set: setLoading } = useLogicalDayLoading();
    const { value: selectedDate } = useSelectedDate();
    const { set: setLogicalDaysAtom } = useLogicalDaysAtom();
    const { set: setResourcesOpeningRanges } = useResourceOpeningRanges();
    const { get: filteredAgendaAppointments } = useFilteredAgendaBookings();
    const { get: filteredAgendaDiscounts } = useFilteredAgendaDiscounts();
    const { value: agendaSettings } = useAgendaSettingsAtom();
    const {
        get: selectedResourcesByAgendaView,
    } = useSelectedResourcesByAgendaView();

    const fetchCurrentLogicalDay = useRecoilCallback(
        () => async (
            selectedResources: AgendaResource[] = selectedResourcesByAgendaView,
        ) => {
            setLoading(true);
            const dates =
                window.innerWidth >= WINDOW_MD_PX
                    ? getDatesBasedOnView(
                        agendaSettings.desktopView,
                        selectedDate,
                    )
                    : [selectedDate];

            const logicalDaysResponse = await getLogicalDaysByResourceIds(
                dates[0],
                dates[dates.length - 1],
                selectedResources,
            );

            const { logicalDays, groupOpeningRanges } = groupLogicalDaysByDayId(
                logicalDaysResponse,
                dates,
            );

            setResourcesOpeningRanges(groupOpeningRanges);
            setLogicalDaysAtom(logicalDays);

            setLoading(false);
        },
        [selectedDate, agendaSettings.desktopView],
    );

    return {
        isLoading,
        fetchCurrentLogicalDay,
        filteredAgendaAppointments,
        filteredAgendaDiscounts,
    };
};

export default useLogicalDay;
